/**
 * Client-side thumbnail capture for HTML artifacts.
 *
 * Why client-side: server-side headless Chrome would force a separate Render
 * service (≥ $7/mo, 500 MB memory). The user wanted "real preview as Slack
 * og:image" without that infra cost, so we capture in the uploader's browser
 * right after the artifact is saved.
 *
 * Pipeline:
 *  1. Render the HTML in a hidden srcdoc iframe at 1200×630 (Slack card size).
 *     srcdoc is parsed in the same origin as the host, so html-to-image can
 *     reach the iframe's document directly.
 *  2. Wait for load + a short settle window so fonts and images paint.
 *  3. Rasterize via html-to-image → PNG Blob.
 *  4. Composite a "▪ Leferi Apps · Artifacts" wordmark badge in the top-right
 *     corner via Canvas2D.
 *  5. POST to /api/artifacts/:id/thumbnail.
 *
 * Failure modes are non-fatal — the artifact still ships, og:image just
 * falls back to the SVG card.
 *
 *  - Tainted canvas: any external image without CORS headers causes
 *    html-to-image to throw a SecurityError. We swallow and skip.
 *  - Load timeout: capped at 15s; some pages never fire `load`.
 *  - Webfont flash: 600 ms settle wait covers most cases.
 */
import { toBlob } from 'html-to-image'

const W = 1200
const H = 630
const SETTLE_MS = 600
const LOAD_TIMEOUT_MS = 15000

export async function captureAndUploadThumbnail(
  artifactId: string,
  html: string,
): Promise<void> {
  try {
    const png = await captureHtml(html)
    if (!png) return
    await uploadThumbnail(artifactId, png)
  } catch (err) {
    // Non-fatal: the artifact already exists; og:image just won't be the
    // real-render version this round. Log so the user knows it tried.
    console.warn('[thumbnail] capture/upload failed (non-fatal)', err)
  }
}

async function captureHtml(html: string): Promise<Blob | null> {
  const iframe = document.createElement('iframe')
  iframe.setAttribute('aria-hidden', 'true')
  iframe.style.cssText = `position:fixed;left:-9999px;top:0;width:${W}px;height:${H}px;border:0;visibility:hidden;`
  iframe.srcdoc = html
  document.body.appendChild(iframe)

  try {
    await waitForLoad(iframe, LOAD_TIMEOUT_MS)
    await sleep(SETTLE_MS)

    const doc = iframe.contentDocument
    const target = doc?.documentElement
    if (!doc || !target) return null

    const blob = await toBlob(target, {
      width: W,
      height: H,
      pixelRatio: 1,
      cacheBust: false,
      backgroundColor: '#FFFFFF',
    })
    if (!blob) return null

    return await overlayWordmark(blob)
  } finally {
    iframe.remove()
  }
}

function waitForLoad(iframe: HTMLIFrameElement, timeoutMs: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error('iframe load timeout'))
    }, timeoutMs)
    iframe.addEventListener(
      'load',
      () => {
        window.clearTimeout(timer)
        resolve()
      },
      { once: true },
    )
  })
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => window.setTimeout(r, ms))
}

async function overlayWordmark(captured: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(captured)
  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = H
  const ctx = canvas.getContext('2d')
  if (!ctx) return captured

  ctx.fillStyle = '#FFFFFF'
  ctx.fillRect(0, 0, W, H)
  ctx.drawImage(bitmap, 0, 0, W, H)

  // Wordmark badge — top-right corner.
  // Box: brown 0.85 alpha so it reads on light + dark page captures.
  const padX = 24
  const padY = 24
  const boxW = 268
  const boxH = 40
  const x0 = W - padX - boxW
  const y0 = padY

  ctx.fillStyle = 'rgba(53, 38, 40, 0.88)'
  ctx.fillRect(x0, y0, boxW, boxH)

  // Pink micro-square (▪)
  ctx.fillStyle = '#FC8FFF'
  ctx.fillRect(x0 + 12, y0 + 12, 16, 16)

  // Text. Pretendard isn't guaranteed loaded inside the host page either;
  // the system fallback gives a reasonable serif/sans. The badge is a brand
  // mark, not editorial type — readability matters more than the exact face.
  ctx.fillStyle = '#FFFFFF'
  ctx.font =
    '500 15px "Pretendard", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif'
  ctx.textBaseline = 'middle'
  ctx.textAlign = 'left'
  ctx.fillText('Leferi Apps · Artifacts', x0 + 38, y0 + boxH / 2 + 1)

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (b) resolve(b)
        else reject(new Error('canvas.toBlob returned null'))
      },
      'image/png',
    )
  })
}

async function uploadThumbnail(artifactId: string, png: Blob): Promise<void> {
  const res = await fetch(`/api/artifacts/${artifactId}/thumbnail`, {
    method: 'POST',
    headers: { 'Content-Type': 'image/png' },
    credentials: 'same-origin',
    body: png,
  })
  if (!res.ok && res.status !== 204) {
    throw new Error(`thumbnail upload failed: ${res.status}`)
  }
}
